import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { useDispatch, useSelector } from "react-redux";
import { OrderInfo } from "../../../components/dashboard/orders/order-info";
import { deleteOrderAsync, getOrderAsync } from "../../../slices/order";
import { toast } from "react-hot-toast";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { FolderIcon, LinkIcon, TrashIcon } from "@heroicons/react/24/outline";
import { StripePaymentStatus } from "../../../components/dashboard/stripe/stripe-payment-status";
import { InvoiceStatus } from "../../../components/dashboard/invoice/invoice-status";
import { Notes } from "../../../components/dashboard/notes/notes";
import { dispatchWithToast } from "../../../components/Utils";
import ProConfirmation from "../../../components/utils/ProConfirmation";
import { OrderStatus } from "../../../components/dashboard/orders/utils";
import { ResourceError } from "../../../components/utils/ResourceError";
import { HistoriesModalButton } from "../../../components/dashboard/histories/modal-button";
import { sendStripePaymentLink } from "../../../api/order";

export const Order = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const itemState = {
    ...useSelector((store) => {
      return store.orders;
    }),
  };
  const { isLoading, error, errors, order } = itemState;
  const [isSendingStripeLink, setIsSendingStripeLink] = useState(false);

  const [deletePromptOpen, setDeletePromptOpen] = useState(false);

  useEffect(() => {
    dispatch(getOrderAsync(id));
  }, [dispatch]);

  const handleDeletePrompt = () => {
    setDeletePromptOpen(true);
  };

  const navigate = useNavigate();

  const handleAfterDispatch = () => {
    setDeletePromptOpen(false);
    // Redirect to orders page
    navigate("/dashboard/orders");
  };

  const handleDeleteItem = () => {
    dispatchWithToast(dispatch(deleteOrderAsync(order?.id)), null, () =>
      handleAfterDispatch(false)
    );
  };

  const handleSendStripePaymentLink = async () => {
    if (!order?.id || isSendingStripeLink) return;

    try {
      setIsSendingStripeLink(true);
      const response = await sendStripePaymentLink(order.id);

      if (response?.error) {
        throw new Error(response.error);
      }

      if (response?.session_url && navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(response.session_url);
      }

      toast.success(
        response?.session_url
          ? "Stripe-betalingslenke sendt og kopiert til utklippstavlen"
          : "Stripe-betalingslenke sendt"
      );
      dispatch(getOrderAsync(id));
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          error?.message ||
          "Kunne ikke sende Stripe-betalingslenke"
      );
    } finally {
      setIsSendingStripeLink(false);
    }
  };

  const headerMeta = [
    {
      component: <OrderStatus order={order} />,
      label: "Status",
      description: "",
      path: null,
      action: null,
      icon: null,
      hidden: false,
    },
    {
      component: <StripePaymentStatus order={order} />,
      label: "Betalt med Stripe",
      description: "",
      path: null,
      action: null,
      icon: null,
      hidden: false,
    },
    {
      component: <InvoiceStatus order={order} />,
      label: "Faktura status",
      description: "",
      path: null,
      action: null,
      icon: null,
      hidden: false,
    },
  ];

  const headerActions = [
    {
      component: (
        <div className="px-2 mt-2">
          <Notes module={"order"} module_param={order?.id} />
        </div>
      ),
      label: "Status",
      description: "",
      path: null,
      action: null,
      icon: null,
      textColor: "text-teal-700",
      bgColor: "bg-red-200",
      hidden: false,
      menu: false,
    },
    {
      component: null,
      label: "Send Stripe-lenke",
      description: "",
      path: null,
      action: () => handleSendStripePaymentLink(),
      icon: LinkIcon,
      textColor: "text-teal-700",
      bgColor: "",
      hidden: !!order?.invoice?.id || isLoading,
      disabled: isSendingStripeLink,
      loading: isSendingStripeLink,
      menu: true,
    },
    {
      component: null,
      label: "Kopier",
      description: "",
      path: null,
      // action: () => handleDeletePrompt(order),
      icon: FolderIcon,
      textColor: "text-teal-700",
      bgColor: "",
      hidden: isLoading,
      menu: true,
    },
    {
      component: null,
      label: "Delete",
      description: "",
      path: null,
      action: () => handleDeletePrompt(order),
      icon: TrashIcon,
      textColor: "text-teal-700",
      bgColor: "",
      hidden: order?.invoice?.id || isLoading,
      menu: true,
    },
    {
      component: (
        <HistoriesModalButton module="order" module_param={order?.id} />
      ),
      path: null,
      hidden: false,
      menu: false,
    },
  ];

  const renderContent = () => {
    if (isLoading) {
      return <ResourceLoading />;
    }
    if (error && errors) {
      return <ResourceError errors={errors} />;
    }

    return (
      <div className="w-full">
        <OrderInfo order={order} showWarehousePick={true} />
      </div>
    );
  };

  return (
    <>
      <DashboardHeader
        subtitle=""
        actions={headerActions}
        title={order?.id ? order?.displayTitle : "New Order"}
        meta={headerMeta}
        // mainButton={{
        //   label: "New",
        //   action: () => setCaseDialogOpen(true),
        //   path: null,
        //   color: "",
        //   icon: PlusIcon,
        // }}
      >
        <div className="mx-auto mt-6 w-full max-w-[1800px] px-4 sm:px-6 xl:px-8">
          <div className="space-y-6">{renderContent()}</div>
        </div>
      </DashboardHeader>

      <ProConfirmation
        severity="confirmation"
        open={deletePromptOpen}
        message={
          "Denne ordren vil da bli fjernet for godt og kan ikke reverseres"
        }
        action="delete"
        module="order"
        // item_id={confirmation.item_id}
        yesCallback={() => handleDeleteItem()}
        cancelCallback={() => setDeletePromptOpen(false)}
      />
    </>
  );
};
